import { cn } from '@/utils/cn';
import { useAvatar } from '@/features/profile/contexts/AvatarContext';

interface AvatarProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
};

/**
 * User Avatar - Shows the user's selected avatar from profile
 */
export const UserAvatar = ({ size = 'sm', className }: AvatarProps) => {
  const { selectedAvatar } = useAvatar();

  return (
    <div
      className={cn(
        sizeClasses[size],
        'rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden',
        'bg-gradient-to-br from-soft-neutral-200 to-soft-neutral-300',
        'shadow-sm',
        className
      )}
    >
      {selectedAvatar ? (
        <img
          src={selectedAvatar}
          alt="آواتار کاربر"
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback to generic icon if image fails to load
            e.currentTarget.style.display = 'none';
            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
            if (fallback) fallback.style.display = 'block';
          }}
        />
      ) : null}
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className={cn(
          size === 'sm' ? 'w-3.5 h-3.5' : size === 'md' ? 'w-4 h-4' : 'w-6 h-6',
          'text-muted',
          selectedAvatar ? 'hidden' : 'block'
        )}
      >
        {/* User head */}
        <circle cx="12" cy="8" r="4" fill="currentColor" />
        {/* User body */}
        <path
          d="M4 20C4 16.6863 7.58172 14 12 14C16.4183 14 20 16.6863 20 20"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </div>
  );
};

/**
 * Crown/Badge icon for premium status
 */
export const PremiumBadge = ({ className }: { className?: string }) => {
  return (
    <div
      className={cn(
        'w-5 h-5 rounded-full flex items-center justify-center',
        'bg-gradient-to-br from-[#655adb] to-[#7c6fe8]',
        className
      )}
    >
      <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3">
        <path
          d="M12 2L14.5 8L21 9L16.5 14L18 21L12 17.5L6 21L7.5 14L3 9L9.5 8L12 2Z"
          fill="white"
        />
      </svg>
    </div>
  );
};

export default { UserAvatar, PremiumBadge };
